import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleAuth";
import { PromoBanner } from "@/components/PromoBanner";

const input =
  "w-full bg-[#0b0e13] border border-[#334155] focus:border-[#f59e0b] outline-none px-4 py-4 text-base text-[#f8fafc] placeholder:text-[#64748b] transition-colors";

export default function HardHat() {
  const { user, isAuthed, register, setShowAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ref = localStorage.getItem("rigcalc_ref") || undefined;
      await register(form.email, form.password, form.name || undefined, ref, "hardhat");
      toast.success("7-day free trial started");
      navigate("/kill-sheet");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Signup failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="hardhat-page" className="min-h-screen bg-[#0b0e13] text-[#f8fafc] flex flex-col">
      <div className="h-1.5 bg-[#f59e0b]" />
      <main className="flex-1 w-full max-w-md mx-auto px-6 py-10 flex flex-col">
        <div className="flex items-center gap-3 mb-10">
          <img src="/icon-192.png" alt="RIGCALC" className="w-11 h-11" />
          <span className="heading text-xl font-bold">RIGCALC</span>
        </div>
        <div className="mono text-[10px] tracking-[0.3em] text-[#f59e0b] mb-3">SCANNED FROM A HARD HAT</div>
        <h1 className="heading text-4xl sm:text-5xl font-bold leading-[1.05] mb-8">
          7 days free.<br /><span className="text-[#f59e0b]">No card.</span>
        </h1>
        <PromoBanner className="mb-6 -mt-2" />

        {isAuthed ? (
          <div data-testid="hardhat-signed-in" className="space-y-4">
            <p className="text-[#94a3b8] text-sm">You're signed in as {user.email}.</p>
            <Link to="/kill-sheet" data-testid="hardhat-open-app" className="flex items-center justify-center gap-2 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] py-4 mono text-sm tracking-widest uppercase font-bold transition-colors">
              Open RIGCALC <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="hardhat-form">
            <GoogleButton source="hardhat" testid="hardhat-google-btn" />
            <div className="flex items-center gap-3 py-1 text-[10px] mono tracking-widest text-[#64748b]">
              <div className="flex-1 h-px bg-[#334155]" /> OR <div className="flex-1 h-px bg-[#334155]" />
            </div>
            <input data-testid="hardhat-name" className={input} placeholder="Name (optional)" value={form.name} onChange={set("name")} autoComplete="name" />
            <input data-testid="hardhat-email" className={input} type="email" required placeholder="Email" value={form.email} onChange={set("email")} autoComplete="email" inputMode="email" />
            <input data-testid="hardhat-password" className={input} type="password" required minLength={8} placeholder="Password (8+ characters)" value={form.password} onChange={set("password")} autoComplete="new-password" />
            <button data-testid="hardhat-start-trial" type="submit" disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#f59e0b] hover:bg-[#fbbf24] active:scale-[0.99] text-[#0b0e13] py-5 mono text-sm tracking-widest uppercase font-bold transition-[background-color,transform] disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start free trial <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-center text-xs text-[#64748b] pt-2">
              Already have an account? <button type="button" onClick={() => setShowAuth(true)} data-testid="hardhat-login-link" className="text-[#f59e0b]">Sign in</button>
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
